/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useEffect } from "react";
import { Box } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { Formik, Form } from "formik";
import {
  BreadcrumbComponent,
  CardComponent,
  CustomTabPanelComponent,
  DescriptionInputComponent,
} from "../../../../common/components";
import { customerCreate, getUserDetails } from "../../../../api/customer";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { initialValues, validationSchema } from "./create-customer-helper";
import CreateCustomerPrimaryDetails from "./create-customer-primary-details/create-customer-primary-details";
import CreateCustomerAdditionalDetails from "./create-customer-additional-details/create-customer-additional-details";
import CreateCustomerAddressDetails from "./create-customer-address-details/create-customer-address-details";
import descriptionIcon from "../../../../assets/icons/description.svg";
import "./create-customer.scss";
import { useLeadOwnerOptions } from "../../../../utils/getLeadOwnerOptions";
import { getCurrentUser } from "../../../../common/routines/user-operations";

interface OwnerOption {
  value: string;
  label: string;
  email: string;
  avatar?: string | null;
}

const CreateCustomer = (props: any) => {
  const {
    createCustomerInitialValues,
    setCreateCustomerInitialValues,
    setIsShowCreateCustomerSection,
    updateCustomerList,
    setAlertOpen,
    setAlertMessage,
    setAlertType,
  } = props;

  const breadcrumbLink = [
    { label: "Customer Management", link: "#", handleClick: () => {} },
    {
      label: "Customer Profile",
      link: "/home/customer-management/customer-details",
      handleClick: () => {
        setIsShowCreateCustomerSection(false);
        setLoading(false);
        setCreateCustomerInitialValues(initialValues);
        setSelectedLeadSource("Manual");
        setPreferredContactMode(null);
        setPreviewImage(false);
      },
    },
    { label: "Create Customer", link: "#", handleClick: () => {} },
  ];
  const [preferredContactMode, setPreferredContactMode] = useState<
    string | null
  >("No Preference");
  const [selectedLeadSource, setSelectedLeadSource] = useState("Manual");
  const [loading, setLoading] = useState<boolean>(false);
  const [isPreviewImage, setPreviewImage] = useState(false);
  const [indexValue, setIndexValue] = React.useState(0);
  const [isNavigate, setIsNavigate] = useState(false);
  const [selectedCustomerOwner, setSelectedCustomerOwner] = useState<OwnerOption>({
    value: "",
    label: "",
    email: "",
    avatar: null,
  });
  const [leadOptions, setLeadOptions] = useState<OwnerOption[]>([]);
  const [checked, setChecked] = React.useState(false);  const currentUser = getCurrentUser();
  useEffect(() => {
    if (leadOptions?.length) {
      const matchedOwner = leadOptions.find(option => parseInt(option.value) === currentUser.id);
      if (matchedOwner) {
        setSelectedCustomerOwner(matchedOwner);
      } else {
        setSelectedCustomerOwner(leadOptions[0]);
      }
    }
  }, [leadOptions, currentUser.id]);

  const handleCancel = async () => {
    setCreateCustomerInitialValues(initialValues);
    setIsShowCreateCustomerSection(false);
  };

  useEffect(() => {
    const fetchCreatedByOptions = async () => {
      try {
        const response = await getUserDetails({}, "Create Campaigns");
        const data = response.data.data.data.data;
        const formattedOptions: OwnerOption[] = data.map((user: any) => ({
          value: String(user.id),
          label: `${user.first_name} ${user.last_name}`,
          email: user.email,
          avatar: user.profile_image_url || null,
        }));
 
        setLeadOptions(formattedOptions);
 
        const currentUserId = getCurrentUser();
        const matchedUser = formattedOptions.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setSelectedCustomerOwner(matchedUser);
        } else if (formattedOptions.length > 0) {
          setSelectedCustomerOwner(formattedOptions[0]);
        }
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message ||
            "Something went wrong please try again."
        );
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    };
 
    fetchCreatedByOptions();
  }, []);

  const handleSave = async (values: any, resetForm: any) => {
    setLoading(true);
    try {
      const formData = new FormData();
      const customerDetails = {
        company: values.company || null,
        firstName: values.firstName,
        lastName: values.lastName,
        primaryEmail: values.email,
        secondaryEmail: values.secondaryEmail || null,
        primaryPhoneNumber: values?.primaryPhoneNumber || null,
        secondaryPhoneNumber: values?.secondaryPhoneNumber || null,
        leadSource: selectedLeadSource || null,
        faxNumber: values.fax || null,
        assistant: values.assistant || null,
        assistantPhoneNumber: values.assistantPhoneNumber || null,
        description: values.description || null,
        middleName: values.middleName || null,
        preferredContactMode: preferredContactMode
          ? preferredContactMode
          : null,
        customerOwnerId: selectedCustomerOwner?.value || null,
        dob: values.dob
          ? new Date(values.dob).toISOString()
          : null,
      };

      // Append all fields except addresses
      Object.entries(customerDetails).forEach(([key, value]) => {
        if (
          key !== "mailingAddress" &&
          key !== "permanentAddress" &&
          value !== null &&
          value !== undefined
        ) {
          formData.append(key, value);
        }
      });

      // Helper to check if any address field is present
      const hasAddressValues = (address: any) =>
        address &&
        Object.values(address).some(
          (v) => v !== null && v !== undefined && v !== ""
        );

        ["streetLine1", "streetLine2", "city", "state", "country", "zipcode"].forEach((field) => {
          const mailingValue = values?.mailingAddress?.[field];
          if (mailingValue) {
            formData.append(`mailingAddress[${field}]`, mailingValue);
          }
        
          const permanentValue = values?.permanentAddress?.[field];
          if (permanentValue) {
            formData.append(`permanentAddress[${field}]`, permanentValue);
          }
        });

      if (values.imageUrl instanceof File) {
        formData.append("customerImg", values.imageUrl);
      }
      const response = await customerCreate(formData);

      if (response.data.success) {
        setLoading(false);
        setCreateCustomerInitialValues(initialValues);
        setAlertMessage("Customer saved successfully!");
        setAlertType("success");
        setAlertOpen(true);
        setSelectedLeadSource("Manual");
        setPreferredContactMode("No Preference");
        setPreviewImage(false);
        const currentUserId = getCurrentUser();
        const matchedUser = leadOptions.find(
          (opt) => opt.value === String(currentUserId?.id)
        );
        if (matchedUser) {
          setSelectedCustomerOwner(matchedUser);
        } else if (leadOptions.length > 0) {
          setSelectedCustomerOwner(leadOptions[0]);
        }
        setChecked(false);
        setTimeout(() => {
          if (isNavigate) {
            setIsShowCreateCustomerSection(false);
          }
          setCreateCustomerInitialValues(initialValues);
          resetForm();
          updateCustomerList();
        }, 100);
      } else {
        setAlertMessage("Failed to save lead.");
        setAlertType("error");
        setAlertOpen(true);
        setLoading(false);
      }
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead."
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const handleMainTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setIndexValue(newValue);
  };

  return (
    <>
      <div className="leadcreate-wrapper">
        <div className="breadcrumb-wrapper">
          <BreadcrumbComponent
            breadcrumbLink={breadcrumbLink}
            showBackArrow={true}
            handleBackArrowClick={() => {
              setCreateCustomerInitialValues(initialValues);
              setIsShowCreateCustomerSection(false);
            }}
          />
        </div>
        <div className="customer-create-filter-wrapper">
          <Formik
            initialValues={createCustomerInitialValues || initialValues}
            validationSchema={validationSchema(false)}
            enableReinitialize={true}
            onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              validateField,
              setFieldTouched,
              touched,
              errors,
              submitCount,
            }) => (
              <Form>
                <div className="customer-create-tab-section">
                  <div className="customer-create-tab-name-section">
                    <Box>
                      <Tabs
                        value={indexValue}
                        onChange={handleMainTabChange}
                        allowScrollButtonsMobile
                      >
                        <Tab label="Primary Details" />
                        <Tab label="Additional Details" />
                        <Tab label="Address Information" />
                      </Tabs>
                    </Box>
                  </div>
                  <div className="customer-create-tab-data-section">
                    <CustomTabPanelComponent value={indexValue} index={0}>
                      <CreateCustomerPrimaryDetails
                        setFieldValue={setFieldValue}
                        values={values}
                        setSelectedLeadSource={setSelectedLeadSource}
                        selectedLeadSource={selectedLeadSource}
                        setPreviewImage={setPreviewImage}
                        isPreviewImage={isPreviewImage}
                        validateField={validateField}
                        setFieldTouched={setFieldTouched}
                        selectedCustomerOwner={selectedCustomerOwner}
                        setSelectedCustomerOwner={setSelectedCustomerOwner}
                        LEAD_OWNER_OPTIONS={leadOptions}
                        errors={errors}
                        touched={touched}
                        submitCount={submitCount}
                      />
                    </CustomTabPanelComponent>
                    <CustomTabPanelComponent value={indexValue} index={1}>
                      <CreateCustomerAdditionalDetails
                        setFieldValue={setFieldValue}
                        values={values}
                        setSelectedLeadSource={setSelectedLeadSource}
                        selectedLeadSource={selectedLeadSource}
                        preferredContactMode={preferredContactMode}
                        setPreferredContactMode={setPreferredContactMode}
                        selectedCustomerOwner={selectedCustomerOwner}
                        setSelectedCustomerOwner={setSelectedCustomerOwner}
                        touched={touched}
                        validateField={validateField}
                        setFieldTouched={setFieldTouched}
                        errors={errors}
                      />
                    </CustomTabPanelComponent>
                    <CustomTabPanelComponent value={indexValue} index={2}>
                      <CreateCustomerAddressDetails
                        setFieldValue={setFieldValue}
                        values={values}
                        setSelectedLeadSource={setSelectedLeadSource}
                        selectedLeadSource={selectedLeadSource}
                        selectedCustomerOwner={selectedCustomerOwner}
                        setSelectedCustomerOwner={setSelectedCustomerOwner}
                        checked={checked}
                        setChecked={setChecked}
                      />
                    </CustomTabPanelComponent>
                  </div>
                </div>
                <div className="customer-form-wrapper">
                  <CardComponent title="Description" icon={descriptionIcon}>
                    <div className="form-grid-three">
                      <DescriptionInputComponent
                        fieldName="description"
                        label="Company Description"
                        placeholder="Add Description"
                        optional={false}
                        disabled={false}
                        value={values.description}
                        rows={8}
                        onChange={(e: any) =>
                          setFieldValue("description", e?.target?.value)
                        }
                        maxlength={150}
                      />
                    </div>
                  </CardComponent>
                </div>
                <Box className="footer">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      handleSubmit();
                      setIsNavigate(false);
                    }}
                  >
                    Save & New
                  </button>
                  <button
                    type="button"
                    className="cancel-button next-button-wrapper"
                    onClick={() => {
                      handleSubmit();
                      setIsNavigate(true);
                    }}
                  >
                    Save
                  </button>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default CreateCustomer;
